import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Property } from '../../../shared/types/Property';
import { BuildingIcon, TagIcon, MapPinIcon, PhoneIcon, GlobeIcon, ClockIcon, CheckIcon, XIcon, InfoIcon } from 'lucide-react';
import { InfoTooltip } from '../../../shared/ui/InfoTooltip';
export function PropertyInformation() {
  const {
    t
  } = useTranslation('rooms');
  // Mock data for a Malaysian island resort
  const [property, setProperty] = useState<Property>({
    name: 'Baa Atoll Lagoon Paradise Resort',
    description: 'Nestled on the pristine shores of Langkawi, Langkawi Lagoon Paradise Resort offers a perfect blend of Malaysian luxury and natural beauty. Surrounded by turquoise lagoons and overlooking the crystal-clear waters of the Andaman Sea, this exclusive retreat provides an authentic Malaysian experience with modern comforts.',
    designation: 'Resort',
    starRating: 5,
    tags: ['Overwater Villas', 'Spa Retreat', 'Family-Friendly', 'Luxury', 'Malaysian Cuisine', 'Beachfront', 'Island Escape', 'Wellness-Retreat', 'All-Inclusive', 'Yoga Classes', 'Eco-Friendly', 'Cultural Experiences'],
    address: {
      office: 'Eydhafushi Island',
      city: 'Baa Atoll',
      postalCode: '06010',
      country: 'Malaysia',
      latitude: '5.1042',
      longitude: '73.0680'
    },
    contact: {
      email: 'reservations@baaatolllagoon.mv',
      phone: '+960 660 1234',
      website: 'https://www.baaatolllagoonresort.mv',
      timezone: 'Asia/Kuala_Lumpur'
    },
    checkIn: '14:00',
    checkOut: '12:00',
    amenities: ['Infinity Pool', 'Private Pool Villas', 'WiFi', 'Airport Shuttle', 'Daily Housekeeping', 'Smart TV', 'Hair Dryer', 'In-Room Safe', 'Bicycle Rental', 'Malaysian Cooking Classes', 'Sunset Cruise', 'Island Hopping', 'Floating Breakfast', 'Traditional Malaysian Massage', 'Bodu Beru Performances', 'Dolphin Cruise Tours'],
    transportation: ['Taxi', 'Hotel Shuttle', 'Motorbike Rental', 'Longtail Boat', 'Ferry', 'Domestic Flight', 'Private Van Transfer']
  });
  // Function to render tags
  const renderTags = (tags: string[]) => {
    return <div className="flex flex-wrap gap-2" data-id="element-10074" data-mp="3bf8b68mjybr3">
        {tags.map((tag, index) => <div key={index} className="flex items-center bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded" data-id="element-10075" data-mp="3bhcmhbms6d1e" data-mpi={index}>
            {tag}
            <button aria-label={`Remove ${tag}`} className="ml-1 text-gray-500 hover:text-gray-700" data-id="element-10076" data-mp="3khrd8qiikkca">
              <XIcon size={12} aria-hidden="true" data-id="element-10077" data-mp="zpyxuzj17n0m" />
            </button>
          </div>)}
      </div>;
  };
  // Draft information tooltip content
  const draftInfoContent = "Your hotel listing is currently in draft mode. It won't be visible until your booking engine is enabled. Once you add availability or offers through your inventory management, our team will review and publish them when ready.";
  return <div className="space-y-6" data-id="element-10078" data-mp="taic3ioa3w3y">
      {/* Simplified header section */}
      <div data-id="element-10079" data-mp="y8s9lzxh78sq">
        <h1 className="text-2xl font-semibold text-gray-800 mb-4" data-id="element-10080" data-mp="y8a2275d585i">
          {t('pages.PropertyInformation.hotel')}
        </h1>
        <div className="flex justify-between items-center" data-id="element-10081" data-mp="1lzp8oc46abmk">
          {/* Left side - tabs */}
          <div className="border-b border-gray-200" data-id="element-10082" data-mp="1qwfr363g9jb5">
            <div className="flex space-x-6" data-id="element-10083" data-mp="1c8kvnhy250ud">
              <button className="text-sm font-medium text-blue-600 border-b-2 border-blue-600 pb-2" data-id="element-10084" data-mp="1c7rwb21fhdm9">
                {t('pages.PropertyInformation.hotel_information')}
              </button>
              <button className="text-sm font-medium text-gray-500 hover:text-gray-800 pb-2" data-id="element-10085" data-mp="24w78lgt6dhtw">
                {t('components.EditRoomForm.media_assets')}
              </button>
            </div>
          </div>
          {/* Right side - status button with tooltip */}
          <div className="relative group" data-id="element-10086" data-mp="h0sm34dddbun">
            <button className="flex items-center bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm font-medium" data-id="element-10087" data-mp="gzrbf109mrdg">
              <span className="mr-2" data-id="element-10088" data-mp="c1pqsbn6yjzv">
                {t('pages.PropertyInformation.draft_submitted_for_review')}
              </span>
              <InfoIcon size={16} className="text-gray-500" data-id="element-10089" data-mp="14pvdv51pwl2v" />
            </button>
            <div className="absolute right-0 mt-2 w-72 p-3 bg-white border border-gray-200 rounded-lg shadow-lg z-10 hidden group-hover:block" data-id="element-10090" data-mp="14rxzlcvu7s1s">
              <h3 className="text-sm font-medium text-gray-800 mb-1" data-id="element-10091" data-mp="uza5ipw8s26r">
                {t('pages.PropertyInformation.draft_status')}
              </h3>
              <p className="text-xs text-gray-600" data-id="element-10092" data-mp="pzecflp31v5z">{draftInfoContent}</p>
            </div>
          </div>
        </div>
      </div>
      {/* Hotel Details Form */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6" data-id="element-10093" data-mp="1ip4id7gk9qr0">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-10094" data-mp="1q1u6ww2a5clv">
          <div data-id="element-10095" data-mp="1v05gd5onxl3k">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10096" data-mp="1uzm15wqedat8">
              {t('pages.PropertyInformation.hotel_name')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.name} onChange={e => setProperty({
            ...property,
            name: e.target.value
          })} data-id="element-10097" data-mp="201bdrgt9pwjf" />
          </div>
          <div data-id="element-10098" data-mp="2ipgfkiqd77fl">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10099" data-mp="2ir16lydh088p">
              {t('pages.PropertyInformation.designation')}
            </label>
            <div className="relative" data-id="element-10100" data-mp="2so5v44usgfso">
              <select className="w-full border border-gray-300 rounded-md px-3 py-2 appearance-none" value={property.designation} onChange={e => setProperty({
              ...property,
              designation: e.target.value
            })} data-id="element-10101" data-mp="2sp76kafpv4ax">
                <option value="Resort" data-id="element-10102" data-mp="16dkwgiaa2xpn">
                  {t('pages.PropertyInformation.resort')}
                </option>
                <option value="Hotel" data-id="element-10103" data-mp="11l9vdc3sx5vi">
                  {t('pages.PropertyInformation.hotel')}
                </option>
                <option value="Villa" data-id="element-10104" data-mp="1g9nxtlxb2sbj">
                  {t('pages.PropertyInformation.villa')}
                </option>
                <option value="Guesthouse" data-id="element-10105" data-mp="1be8on7a8tklg">
                  {t('pages.PropertyInformation.guesthouse')}
                </option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-10106" data-mp="1lbd9n3scra0o">
                <ChevronDownIcon size={16} className="text-gray-400" data-id="element-10107" data-mp="3r4bl6wg9gzyh" />
              </div>
            </div>
          </div>
        </div>
        <div className="mb-6" data-id="element-10108" data-mp="3n3rqdzj5u8fi">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10109" data-mp="3cy08pbvsptn8">
            {t('components.AddRoomForm.description')}
          </label>
          <textarea className="w-full border border-gray-300 rounded-md px-3 py-2 h-24" value={property.description} onChange={e => setProperty({
          ...property,
          description: e.target.value
        })} data-id="element-10110" data-mp="3gytvu31ez4f2" />
        </div>
        <div className="mb-6" data-id="element-10111" data-mp="2tc3wj9gg9ny5">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10112" data-mp="2y9digf4mrhh8">
            {t('pages.PropertyInformation.star_rating')}
          </label>
          <div className="relative" data-id="element-10113" data-mp="2kaxaylyy1my1">
            <select className="w-full border border-gray-300 rounded-md px-3 py-2 appearance-none" value={property.starRating} onChange={e => setProperty({
            ...property,
            starRating: parseInt(e.target.value)
          })} data-id="element-10114" data-mp="2p7w34yz1rjtl">
              <option value="5" data-id="element-10115" data-mp="1w0q5mjo58jdc">5</option>
              <option value="4" data-id="element-10116" data-mp="1r2f02l4vsv0x">4</option>
              <option value="3" data-id="element-10117" data-mp="1r3g96k2c77g0">3</option>
              <option value="2" data-id="element-10118" data-mp="1r4hq3561ao5n">2</option>
              <option value="1" data-id="element-10119" data-mp="1madeg8ltpry8">1</option>
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-10120" data-mp="1i9tgx3xc8mbk">
              <ChevronDownIcon size={16} className="text-gray-400" data-id="element-10121" data-mp="1daqp4l6dcclj" />
            </div>
          </div>
        </div>
        <div className="mb-6" data-id="element-10122" data-mp="18b4biammouc7">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10123" data-mp="18avz0juf3r1j">
            {t('pages.PropertyInformation.tags')}
          </label>
          {renderTags(property.tags)}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-10124" data-mp="zc0oivkcbusf">
          <div data-id="element-10125" data-mp="2xqbskqvuz46b">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10126" data-mp="2xqvcgd52k2mz">
              {t('pages.PropertyInformation.address')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.address.office} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              office: e.target.value
            }
          })} data-id="element-10127" data-mp="2tmvwpwrm57uo" />
          </div>
          <div data-id="element-10128" data-mp="3sp2j8he0pysq">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10129" data-mp="3spc27l0nb5ox">
              {t('pages.PropertyInformation.area_name')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value="Pantai Cenang" data-id="element-10130" data-mp="5ijn8nz70m20" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-10131" data-mp="1gyzwfzk5m6gf">
          <div data-id="element-10132" data-mp="1h0kp1jmwrfbn">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10133" data-mp="1lx1gampem9sy">
              {t('pages.PropertyInformation.city')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.address.city} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              city: e.target.value
            }
          })} data-id="element-10134" data-mp="6wrmmxpe6m3y" />
          </div>
          <div data-id="element-10135" data-mp="3p2e4zlu2cyc2">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10136" data-mp="3p3yz5acguf9e">
              {t('pages.PropertyInformation.postal_code')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.address.postalCode} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              postalCode: e.target.value
            }
          })} data-id="element-10137" data-mp="1y80oxastabl" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6" data-id="element-10138" data-mp="2tvwza063x0x4">
          <div data-id="element-10139" data-mp="2p1srjep0oouz">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10140" data-mp="2p19cc5qr4ekn">
              {t('pages.PropertyInformation.country')}
            </label>
            <div className="relative" data-id="element-10141" data-mp="2f5fs006sjgsb">
              <select className="w-full border border-gray-300 rounded-md px-3 py-2 appearance-none" value={property.address.country} onChange={e => setProperty({
              ...property,
              address: {
                ...property.address,
                country: e.target.value
              }
            })} data-id="element-10142" data-mp="2f6h9onibb1j0">
                <option value="Malaysia" data-id="element-10143" data-mp="3viwh0eeelib1">
                  {t('pages.PropertyInformation.malaysia')}
                </option>
                <option value="Sri Lanka" data-id="element-10144" data-mp="igu03c58hin2">
                  {t('pages.PropertyInformation.indonesia')}
                </option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-10145" data-mp="9hyuo2a0kckt">
                <ChevronDownIcon size={16} className="text-gray-400" data-id="element-10146" data-mp="egjrdrq24e4i" />
              </div>
            </div>
          </div>
          <div data-id="element-10147" data-mp="127fiemmov5ap">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10148" data-mp="127p623k9h0d8">
              {t('pages.PropertyInformation.latitude')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.address.latitude} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              latitude: e.target.value
            }
          })} data-id="element-10149" data-mp="s8ghxrc2ufys" />
          </div>
          <div data-id="element-10150" data-mp="3ac6b9mc0m6o5">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10151" data-mp="3afu5knpaax3x">
              {t('pages.PropertyInformation.longitude')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.address.longitude} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              longitude: e.target.value
            }
          })} data-id="element-10152" data-mp="3jde6fvj8ire0" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-10153" data-mp="2vola3xypahiv">
          <div data-id="element-10154" data-mp="2vna19jqeejkw">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10155" data-mp="27z8zqurn03ez">
              {t('pages.PropertyInformation.contact_email')}
            </label>
            <input type="email" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.contact.email} onChange={e => setProperty({
            ...property,
            contact: {
              ...property.contact,
              email: e.target.value
            }
          })} data-id="element-10156" data-mp="2305ub97qvv6h" />
          </div>
          <div data-id="element-10157" data-mp="2zefiisq0s8qx">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10158" data-mp="2zcupx8n9mzvp">
              {t('pages.PropertyInformation.phone_number')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.contact.phone} onChange={e => setProperty({
            ...property,
            contact: {
              ...property.contact,
              phone: e.target.value
            }
          })} data-id="element-10159" data-mp="39c3flpb3lsc9" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-10160" data-mp="13vom884n3vt">
          <div data-id="element-10161" data-mp="3sfng3r3e7wwo">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10162" data-mp="3sf5l0xsgvoqc">
              {t('pages.PropertyInformation.website')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.contact.website} onChange={e => setProperty({
            ...property,
            contact: {
              ...property.contact,
              website: e.target.value
            }
          })} data-id="element-10163" data-mp="3jgjtid50ozdf" />
          </div>
          <div data-id="element-10164" data-mp="638cmwaqigzz">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10165" data-mp="2vpwjnmbjjzo8">
              {t('pages.PropertyInformation.timezone')}
            </label>
            <div className="relative" data-id="element-10166" data-mp="2qpib83612npo">
              <select className="w-full border border-gray-300 rounded-md px-3 py-2 appearance-none" value={property.contact.timezone} onChange={e => setProperty({
              ...property,
              contact: {
                ...property.contact,
                timezone: e.target.value
              }
            })} data-id="element-10167" data-mp="2s88tdw65xbht">
                <option value="Asia/Kuala_Lumpur" data-id="element-10168" data-mp="29gg0e1r3fqa3">
                  {t('pages.PropertyInformation.asiakualalumpur')}
                </option>
                <option value="Asia/Singapore" data-id="element-10169" data-mp="20hci2zzr3ewn">
                  {t('pages.PropertyInformation.asiasingapore')}
                </option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-10170" data-mp="24ixp44plsyyx">
                <ChevronDownIcon size={16} className="text-gray-400" data-id="element-10171" data-mp="1qkzpk5fxz73h" />
              </div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-10172" data-mp="1bvxxyxm7x36b">
          <div data-id="element-10173" data-mp="1byafj9o2dbiu">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10174" data-mp="16x4l97er9g77">
              {t('pages.PropertyInformation.checkin_time')}
            </label>
            <input type="time" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.checkIn} onChange={e => setProperty({
            ...property,
            checkIn: e.target.value
          })} data-id="element-10175" data-mp="1lv5fzy3wdlzr" />
          </div>
          <div data-id="element-10176" data-mp="24leotqu6wc0f">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10177" data-mp="24l50e7osmcwu">
              {t('pages.PropertyInformation.checkout_time')}
            </label>
            <input type="time" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.checkOut} onChange={e => setProperty({
            ...property,
            checkOut: e.target.value
          })} data-id="element-10178" data-mp="1zo4srscap23m" />
          </div>
        </div>
        <div className="mb-6" data-id="element-10179" data-mp="3kw60rxtwb9c9">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-10180" data-mp="3px3qcvh8j5ns">
            {t('pages.PropertyInformation.hotel_amenities')}
          </label>
          <div className="border border-gray-200 rounded-lg p-4" data-id="element-10181" data-mp="3ty5hep100beo">
            <div className="flex flex-wrap gap-2" data-id="element-10182" data-mp="2x8y4g5eg3amd">
              {property.amenities.map((amenity, index) => <div key={index} className="flex items-center bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded" data-id="element-10183" data-mp="328kiui60ewwr" data-mpi={index}>
                  {amenity}
                  <button aria-label={`Remove ${amenity}`} className="ml-1 text-gray-500 hover:text-gray-700" data-id="element-10184" data-mp="3b0uum3wjzag4">
                    <XIcon size={12} aria-hidden="true" data-id="element-10185" data-mp="2ija71f9dww3x" />
                  </button>
                </div>)}
            </div>
          </div>
        </div>
        <div data-id="element-10186" data-mp="2axwsz1v5nudj">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-10187" data-mp="2axmz336zp32k">
            {t('pages.PropertyInformation.transportation')}
          </label>
          <div className="space-y-2" data-id="element-10188" data-mp="21xzygjm341ak">
            {property.transportation.map((item, index) => <div key={index} className="flex items-center" data-id="element-10189" data-mp="1wzgdalfbetj9" data-mpi={index}>
                <input type="checkbox" id={`transportation-${index}`} checked={true} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-10190" data-mp="1s0vdgn3z6bvc" />

                <label htmlFor={`transportation-${index}`} className="ml-2 text-sm text-gray-700" data-id="element-10191" data-mp="1ibljf1pkmj4l">
                  {item}
                </label>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}
// Helper component for the ChevronDown icon
function ChevronDownIcon({
  size = 24,
  className = ''
}) {
  return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-10192" data-mp="132fzdvt3z2y0">
      <polyline points="6 9 12 15 18 9" data-id="element-10193" data-mp="o55fpz6baqr7"></polyline>
    </svg>;
}